/**
 * Resolve `<img data-key="...">` tags in rich-text HTML to displayable images
 * by injecting `src` attributes from the server-provided `imageUrls` map.
 *
 * Persisted rich-text HTML stores images as:
 *   <img data-key="orgId/clinical-docs/uuid.png" alt="filename.png" style="...">
 *
 * Without a `src`, browsers won't render them. The editable TipTap editor
 * resolves these via the ResizableImage extension, but read-only previews
 * (formBuilder, reportBuilder, formPreview) render raw HTML with
 * `dangerouslySetInnerHTML` and need this helper to inject the signed URL.
 *
 * @param {string} html  - Raw HTML string from the rich-text field value.
 * @param {Object} imageUrls - `{ [dataKey]: signedUrl }` map from the
 *                              backend response envelope.
 * @returns {string} HTML with `src` attributes injected on matching `<img>` tags.
 */
export function resolveRichTextImages(html, imageUrls) {
    if (!html || !imageUrls || typeof imageUrls !== 'object') return html || '';

    // Match <img ...> tags that have a data-key attribute
    return html.replace(/<img\s([^>]*?)>/gi, (fullMatch, attrs) => {
        const keyMatch = attrs.match(/data-key="([^"]*)"/);
        if (!keyMatch) return fullMatch;

        const dataKey = keyMatch[1];
        const url = imageUrls[dataKey];
        if (!url) return fullMatch;

        // If there's already a src attribute, replace it; otherwise inject one
        if (/\bsrc="[^"]*"/.test(attrs)) {
            const updatedAttrs = attrs.replace(/\bsrc="[^"]*"/, `src="${url}"`);
            return `<img ${updatedAttrs}>`;
        }
        return `<img src="${url}" ${attrs}>`;
    });
}
